import { useEffect, useState } from 'react';
import {
  Copy,
  CheckCircle2,
  ExternalLink,
  Code2,
  MessageSquare,
  BookOpen,
} from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { notify } from '../../lib/notify';

interface ApiKey {
  id: number;
  name: string;
  key_prefix: string;
  platform: string;
  scopes: string[];
  is_active: boolean;
}

const PUBLIC_BASE = typeof window !== 'undefined' ? window.location.origin : 'https://soporte.vipsoft.cloud';

export default function Integrations() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKeyId, setSelectedKeyId] = useState<number | null>(null);
  const [color, setColor] = useState('#3b82f6');

  useEffect(() => {
    setLoading(true);
    apiFetch<ApiKey[]>('/api-keys')
      .then((data) => {
        const active = data.filter((k) => k.is_active && k.scopes.includes('tickets:create'));
        setKeys(active);
        const first = active[0];
        if (first) setSelectedKeyId(first.id);
      })
      .catch((err) => notify.apiError(err, 'No se pudieron cargar las API keys.'))
      .finally(() => setLoading(false));
  }, []);

  const selectedKey = keys.find((k) => k.id === selectedKeyId);
  // No tenemos la key clear-text después de creada — solo el prefix. El snippet
  // del widget muestra un placeholder y el usuario reemplaza con su key real
  // (la copió cuando la generó en la pantalla "API Keys"). El placeholder es
  // obvio (NO empieza con vipsoft_) para que un copy/paste accidental falle
  // ruidosamente en el sitio destino en vez de hacer requests con key inválida.
  const keyPlaceholder = '__PEGA_TU_API_KEY_AQUI__';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <Code2 size={16} className="text-slate-500" />
          Integraciones
        </h2>
        <p className="text-sm text-slate-500">
          Cómo conectar sitios externos y bots con tu sistema de tickets. Necesitas una API key
          generada en la pestaña{' '}
          <strong className="text-slate-700">API Keys</strong>.
        </p>
      </div>

      {!loading && keys.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <strong>Todavía no tienes ninguna API key con permiso{' '}
            <code className="font-mono text-xs">tickets:create</code>.</strong>{' '}
          Ve a la pestaña &quot;API Keys&quot; y genera una para empezar a recibir tickets desde
          integraciones externas.
        </div>
      )}

      {!loading && keys.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <label className="block text-xs font-medium text-slate-600 mb-1">
            API Key a usar en los ejemplos
          </label>
          <select
            value={selectedKeyId ?? ''}
            onChange={(e) => setSelectedKeyId(Number(e.target.value))}
            className="w-full max-w-md px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {keys.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name} — {k.platform}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-slate-400 mt-1">
            Los ejemplos de abajo se actualizan con la key seleccionada. La key completa la
            viste solo cuando la generaste — reemplaza la parte &quot;xxx&quot; en el snippet por la
            tuya.
          </p>
        </div>
      )}

      {/* ---------- Widget ---------- */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-lg bg-blue-50 text-blue-700">
            <MessageSquare size={18} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Widget embebible</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Un bloque flotante en la esquina inferior derecha de cualquier sitio web. El
              visitante completa un formulario corto y el ticket llega acá. Peso: 3.7 KB.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
          <div className="space-y-3">
            <Field label="Color de marca" hint="Se aplica al botón flotante y al header del modal.">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-12 rounded border border-slate-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm font-mono rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </Field>

            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">Vista previa</div>
              <div
                className="h-32 rounded-lg border border-slate-200 bg-slate-50 relative overflow-hidden"
                aria-label="Vista previa del widget"
              >
                <div className="absolute bottom-3 right-3 flex items-center gap-2">
                  <div
                    className="h-10 w-10 rounded-full shadow-lg grid place-items-center text-white"
                    style={{ background: color }}
                  >
                    <MessageSquare size={16} />
                  </div>
                </div>
                <div className="absolute top-3 left-3 text-[10px] text-slate-400">
                  Así se ve en la esquina inferior derecha del sitio
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">
              Snippet para pegar antes de <code className="font-mono">&lt;/body&gt;</code>
            </div>
            <CodeBlock
              code={buildWidgetSnippet({
                base: PUBLIC_BASE,
                color,
                platform: selectedKey?.platform ?? 'tu-proyecto',
              })}
            />
            <p className="text-[11px] text-slate-400 mt-2">
              El widget no requiere API key — su protección es captcha + límite de
              solicitudes. La etiqueta <code className="font-mono">data-platform</code> sirve
              para agrupar los tickets que llegan por este sitio.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- API REST ---------- */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700">
            <Code2 size={18} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">API REST</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Para integraciones que crean o consultan tickets programáticamente desde tu
              backend, CRM o automatizaciones.
            </p>
          </div>
        </div>

        <div className="space-y-5 mt-5">
          <EndpointDoc
            method="POST"
            path="/api/v1/tickets"
            description="Crear un ticket. El cliente se crea si no existe (dedupe por email + plataforma)."
            example={`curl -X POST ${PUBLIC_BASE}/api/v1/tickets \\
  -H "X-API-Key: ${keyPlaceholder}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "subject": "Problema con el login",
    "description": "No puedo ingresar desde móvil.",
    "priority": "normal",
    "category": "tecnico",
    "customer": {
      "name": "Juan Pérez",
      "email": "juan@ejemplo.com",
      "phone": "+58 412 1234567"
    }
  }'`}
            requiredScope="tickets:create"
          />

          <EndpointDoc
            method="GET"
            path="/api/v1/tickets"
            description="Listar tickets. Soporta filtros por status, prioridad, canal, asignado y búsqueda."
            example={`curl ${PUBLIC_BASE}/api/v1/tickets?status_group=active&per_page=20 \\
  -H "X-API-Key: ${keyPlaceholder}"`}
            requiredScope="tickets:read"
          />

          <EndpointDoc
            method="GET"
            path="/api/v1/tickets/:codeOrId"
            description="Obtener el detalle completo de un ticket (mensajes, eventos, cliente, asignado)."
            example={`curl ${PUBLIC_BASE}/api/v1/tickets/TCK-2026-000123 \\
  -H "X-API-Key: ${keyPlaceholder}"`}
            requiredScope="tickets:read"
          />

          <EndpointDoc
            method="POST"
            path="/api/v1/tickets/:id/messages"
            description="Agregar un mensaje (público o nota interna) a un ticket existente."
            example={`curl -X POST ${PUBLIC_BASE}/api/v1/tickets/123/messages \\
  -H "X-API-Key: ${keyPlaceholder}" \\
  -H "Content-Type: application/json" \\
  -d '{ "body": "Estamos revisando tu caso.", "is_internal": false }'`}
            requiredScope="messages:create"
          />

          <EndpointDoc
            method="GET"
            path="/api/v1/reports/overview"
            description="Métricas generales: volumen, SLA, tiempos de respuesta."
            example={`curl ${PUBLIC_BASE}/api/v1/reports/overview?days=30 \\
  -H "X-API-Key: ${keyPlaceholder}"`}
            requiredScope="reports:read"
          />
        </div>

        <div className="mt-6 pt-5 border-t border-slate-100 text-xs text-slate-500 space-y-2">
          <div className="flex items-start gap-2">
            <BookOpen size={14} className="text-slate-400 mt-0.5 shrink-0" />
            <div>
              <strong className="text-slate-700">Autenticación:</strong> manda la API key en el
              header <code className="font-mono">X-API-Key</code> o en{' '}
              <code className="font-mono">Authorization: Bearer …</code>.
            </div>
          </div>
          <div className="flex items-start gap-2">
            <ExternalLink size={14} className="text-slate-400 mt-0.5 shrink-0" />
            <div>
              <strong className="text-slate-700">Errores:</strong> el formato siempre es{' '}
              <code className="font-mono">
                {'{ error: { code, message, details?, request_id } }'}
              </code>
              . El código HTTP indica la categoría (400 validación, 401 auth, 403 permisos, 404
              no encontrado, 409 conflicto, 429 rate limit, 5xx error nuestro).
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Email ---------- */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-1">Por correo electrónico</h3>
        <p className="text-sm text-slate-500 mb-4">
          Tus clientes pueden enviar tickets simplemente mandando un correo a la dirección
          configurada del sistema. El correo se convierte en ticket automáticamente, con
          threading por respuestas (Re: …).
        </p>
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm">
          <span className="text-slate-500">Dirección de soporte:</span>{' '}
          <code className="font-mono text-slate-900">tickets@vipsoft.cloud</code>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Compartila públicamente con tus clientes. El sistema chequea la casilla cada 60
          segundos.
        </p>
      </section>
    </div>
  );
}

function buildWidgetSnippet({
  base,
  color,
  platform,
}: {
  base: string;
  color: string;
  platform: string;
}) {
  // El widget público NO requiere API key — su protección es captcha + rate
  // limit + validación de payload. El `platform` etiqueta los tickets para
  // separar por proyecto. La API key es necesaria para llamadas REST desde
  // backends (ver sección "API REST" más abajo).
  return `<script
  src="${base}/widget.js"
  data-color="${color}"
  data-platform="${platform}"
  defer></script>`;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      notify.success('Copiado al portapapeles.');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      notify.error('No se pudo copiar.');
    }
  };

  return (
    <div className="relative group">
      <pre className="text-[11px] font-mono text-slate-700 bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto whitespace-pre max-h-72">
        {code}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 px-2 py-1 text-xs rounded bg-slate-700/80 hover:bg-slate-700 text-white inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition"
      >
        {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
        {copied ? 'Copiado' : 'Copiar'}
      </button>
    </div>
  );
}

function EndpointDoc({
  method,
  path,
  description,
  example,
  requiredScope,
}: {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
  example: string;
  requiredScope: string;
}) {
  const methodColor = {
    GET: 'bg-emerald-100 text-emerald-700',
    POST: 'bg-blue-100 text-blue-700',
    PATCH: 'bg-amber-100 text-amber-700',
    DELETE: 'bg-rose-100 text-rose-700',
  }[method];

  return (
    <div className="border border-slate-100 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold font-mono ${methodColor}`}>
          {method}
        </span>
        <code className="text-sm font-mono text-slate-900">{path}</code>
        <span className="ml-auto text-[10px] text-slate-500">
          Permiso requerido:{' '}
          <code className="font-mono text-slate-700">{requiredScope}</code>
        </span>
      </div>
      <p className="text-xs text-slate-600 mb-3">{description}</p>
      <CodeBlock code={example} />
    </div>
  );
}
